import { createSelector } from '@reduxjs/toolkit'
import { selectBusinessTravellerApprovalPolicyForUser } from './selectBusinessTravellerApprovalPolicyForUser'
import { selectBusinessTravellerAccommodationBudget } from './selectBusinessTravellerAccommodationBudget'
import { selectBusinessTravellerCurrentApprovalRequest } from './selectBusinessTravellerCurrentApprovalRequest'
import { getAccommodationItems } from 'checkout/selectors/view/hotels'
import { checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import { calculateAccommodationBudget } from 'businessTraveller/utils/calculateTripRequestBudget'

/**
 * Selector to determine if we should show the inline trip request form
 * instead of the normal payment form.
 *
 * This applies to ALL accommodation bookings (both single and multi-room)
 * when approval is required and there's no existing approved trip request.
 *
 * TODO: Add flight support in the inline form
 */
export const selectShouldShowInlineTripRequestForm = createSelector(
  selectBusinessTravellerApprovalPolicyForUser,
  selectBusinessTravellerAccommodationBudget,
  getAccommodationItems,
  checkoutAccommodationOfferView,
  selectBusinessTravellerCurrentApprovalRequest,
  (
    approvalPolicy,
    accommodationBudget,
    accommodationItems,
    accommodationOfferViewResult,
    currentApprovalRequest,
  ): boolean => {
    // Only handle accommodation for now
    if (accommodationItems.length === 0) {
      return false
    }

    // If the approved trip request exists (proceed with booking), show payment form
    if (currentApprovalRequest?.status === 'APPROVED') {
      return false
    }

    // No approval policy means no trip request needed
    if (!approvalPolicy) {
      return false
    }

    // If the accommodationOfferViewResult does not have data, return false
    if (!accommodationOfferViewResult.hasRequiredData) {
      return false
    }

    // Calculate budget using the shared util
    const { isOutOfBudget } = calculateAccommodationBudget({
      accommodationItems,
      accommodationViews: accommodationOfferViewResult.data,
      accommodationBudget,
    })

    // Check approval policy scope
    if (approvalPolicy.hotelScope === 'ALWAYS') {
      return true
    }

    if (approvalPolicy.hotelScope === 'OUTOFPOLICYONLY' && isOutOfBudget) {
      return true
    }

    return false
  },
)
